import { useNavigate } from 'react-router-dom'

import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import { CanSeeModulesMarketplaceDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { locale } from 'modules/marketplace/components/utils/locales'
import { useCheckModuleAcquired } from 'modules/marketplace_modules/hooks/useCheckModuleAcquired'
import { useGetDatoModules } from 'modules/marketplace_modules/hooks/useGetDatoModules'
import UpsellButtons from 'modules/product_updates/components/UpsellButtons'
import { ProductUpdate } from 'modules/product_updates/types'

type Props = {
  productUpdate: ProductUpdate
  isProductUpdateAcquired: boolean
}

export const ModuleRelatedModulesCard = ({ productUpdate, isProductUpdateAcquired }: Props) => {
  const navigate = useNavigate()
  const {
    access: { locale: user_locale },
  } = useCurrentSession()
  const { data: policies } = useQuery(CanSeeModulesMarketplaceDocument)
  const canSeeMarketplaceModules = policies?.modules._policies.moduleManageModule.can

  const datoFormatLocale = locale[user_locale]
  const { navigation } = useNavigation()
  const { data: moduleData } = useGetDatoModules({
    locale: datoFormatLocale,
    slug: productUpdate?.productModule.urlIdentifier,
  })

  const effectiveModule1 = productUpdate?.productModule.effectiveModule1
  const effectiveModule2 = productUpdate?.productModule.effectiveModule2
  const effectiveModule3 = productUpdate?.productModule.effectiveModule3

  const isModule1Acquired = useCheckModuleAcquired(effectiveModule1?.chargebeeModuleName || '')
  const isModule2Acquired = useCheckModuleAcquired(effectiveModule2?.chargebeeModuleName || '')
  const isModule3Acquired = useCheckModuleAcquired(effectiveModule3?.chargebeeModuleName || '')

  if (productUpdate.productModule.urlIdentifier === 'factorial-core') return null

  const relatedModules = [effectiveModule1, effectiveModule2, effectiveModule3]
  if (relatedModules.some((module) => !module) && isProductUpdateAcquired) return null

  let recommendedModule = null
  if (!isModule1Acquired && effectiveModule1) {
    recommendedModule = effectiveModule1
  }
  if (!isModule2Acquired && effectiveModule2) {
    recommendedModule = effectiveModule2
  }
  if (!isModule3Acquired && effectiveModule3) {
    recommendedModule = effectiveModule3
  }
  if (!isProductUpdateAcquired) {
    recommendedModule = moduleData[0]
  }

  if (
    (!recommendedModule && !productUpdate && isProductUpdateAcquired) ||
    !canSeeMarketplaceModules
  )
    return null

  if (isProductUpdateAcquired && isModule1Acquired && isModule2Acquired && isModule3Acquired) {
    return null
  }
  const pageSourceValue = !isProductUpdateAcquired ? 'module_banner' : 'recommended_module_banner'

  return (
    <Box paddingY="s12">
      <Divider />
      <Box paddingY="s24">
        <Text size="400" weight="semibold" color="black">
          {isProductUpdateAcquired
            ? i18n.t('product_updates.detail_page.module_related_updates.title_acquired')
            : i18n.t('product_updates.detail_page.module_related_updates.title')}
        </Text>
        <Box className="text-lg text-f1-foreground-secondary" width="s400">
          {i18n.t('product_updates.detail_page.module_related_updates.description')}
        </Box>
      </Box>

      <Box
        flexDirection="column"
        gap="s12"
        borderRadius={{
          all: 'abs016',
        }}
        border={{
          all: {
            width: 's1',
            color: 'grey300',
            style: 'solid',
          },
        }}
      >
        <Box gap="s16" padding="s8" flexDirection="row">
          {recommendedModule && (
            <>
              <Box>
                <Box key={recommendedModule.id}>
                  <img
                    src={recommendedModule.thumbnailImage?.url || ''}
                    alt={recommendedModule.name || ''}
                    width={200}
                    height={200}
                    style={{
                      borderRadius: '12px',
                      width: '100%',
                      height: '130px',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              </Box>
              <Box flexDirection="column" gap="s8" paddingTop="s8">
                <Box>
                  <Text size="300" weight="semibold" color="black">
                    {recommendedModule.name}
                  </Text>
                  <Text size="200" weight="normal" color="grey700">
                    {recommendedModule.shortDescription}
                  </Text>
                </Box>
                <Box flexDirection="row" gap="s8" paddingTop="s8">
                  <UpsellButtons
                    version="factorial-one"
                    chargebeeModuleName={recommendedModule.chargebeeModuleName || ''}
                    productUpdateSlug={productUpdate.slug}
                    featureIdentifier={recommendedModule.slug}
                    pageSource={pageSourceValue}
                    label={i18n.t('product_updates.detail_page.upsell_button')}
                    disableIcon
                  />
                  <Button
                    size="md"
                    variant="outline"
                    label={i18n.t('product_updates.detail_page.module_related_updates.learn_more')}
                    onClick={() => {
                      track(trackingEvents.marketplaceModules.goToModuleDetailPage, {
                        origin: recommendedModule.slug,
                        source: pageSourceValue,
                      })
                      navigate(
                        navigation.discover.marketplaceModules.fullPath() +
                          `/${recommendedModule.slug}?pm=false`
                      )
                    }}
                  />
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}
